import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Users, Eye, Globe, Clock, TrendingUp, MoreVertical, Flame, Check, X } from 'lucide-react';

// Platform compatibility for segment types
const PLATFORM_COMPATIBILITY = {
    custom: { meta: true, google: true, tiktok: true, linkedin: true },
    auto_generated: { meta: true, google: true, tiktok: true, linkedin: false },
    high_intent: { meta: true, google: true, tiktok: true, linkedin: false }
};

const PLATFORM_LABELS = {
    meta: 'Meta',
    google: 'Google',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn'
};

const PLATFORM_COLORS = {
    meta: '#1877F2',
    google: '#4285F4',
    tiktok: '#000000',
    linkedin: '#0A66C2'
};

/**
 * SegmentNode - Visual node for high-intent audience segments in funnel builder
 * Displays intent signals, estimated size, and source stages
 */
const SegmentNode = memo(({ data, selected }) => {
    const {
        name = 'New Segment',
        segmentType = 'custom',
        sourceStages = ['TOFU'],
        intentSignals = {},
        estimatedSize = 0,
        funnelStage = 'MOFU',
        status = 'draft',
        platforms = null, // Target platforms from funnel context
        onEdit,
        onMenuClick
    } = data;

    const stageColors = {
        TOFU: 'var(--color-info)',
        MOFU: 'var(--color-warning)',
        BOFU: 'var(--color-success)'
    };

    const typeLabels = {
        custom: 'Custom',
        auto_generated: 'Auto',
        high_intent: 'High Intent'
    };

    const typeColors = {
        custom: 'var(--color-text-tertiary)',
        auto_generated: 'var(--color-info)',
        high_intent: 'var(--color-success)'
    };

    // Calculate intent score based on enabled signals
    const enabledSignals = Object.values(intentSignals).filter(s => s?.enabled).length;
    const intentStrength = enabledSignals >= 3 ? 'high' : enabledSignals >= 2 ? 'medium' : 'low';

    const intentColors = {
        high: 'var(--color-success)',
        medium: 'var(--color-warning)',
        low: 'var(--color-text-tertiary)'
    };

    const formatSize = (size) => {
        if (size >= 1000000) return `${(size / 1000000).toFixed(1)}M`;
        if (size >= 1000) return `${(size / 1000).toFixed(1)}K`;
        return size.toString();
    };

    return (
        <div
            style={{
                background: 'var(--color-bg-primary)',
                border: selected
                    ? `2px solid ${intentColors[intentStrength]}`
                    : '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                minWidth: '220px',
                boxShadow: selected ? 'var(--shadow-lg)' : 'var(--shadow-md)',
                transition: 'all var(--transition-fast)',
                position: 'relative'
            }}
        >
            {/* Intent strength indicator bar */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: intentColors[intentStrength],
                borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0'
            }} />

            {/* Target handle (top) - receives from campaigns */}
            <Handle
                type="target"
                position={Position.Top}
                id="source-in"
                style={{
                    background: 'var(--color-accent)',
                    width: '10px',
                    height: '10px'
                }}
            />

            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-3)',
                marginTop: 'var(--space-1)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: 'var(--radius-md)',
                        background: `${intentColors[intentStrength]}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {segmentType === 'high_intent' ? (
                            <Flame size={18} style={{ color: intentColors[intentStrength] }} />
                        ) : (
                            <Users size={18} style={{ color: intentColors[intentStrength] }} />
                        )}
                    </div>
                    <div>
                        <div style={{
                            fontSize: 'var(--font-size-xs)',
                            color: typeColors[segmentType],
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontWeight: 600
                        }}>
                            {typeLabels[segmentType]} Segment
                        </div>
                        <div style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)',
                            maxWidth: '140px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {name}
                        </div>
                    </div>
                </div>

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuClick?.();
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 'var(--space-1)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--color-text-tertiary)'
                    }}
                >
                    <MoreVertical size={16} />
                </button>
            </div>

            {/* Intent Signals */}
            <div style={{
                display: 'flex',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-3)'
            }}>
                <div
                    title="Engagement Depth"
                    style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: 'var(--radius-sm)',
                        background: intentSignals.engagementDepth?.enabled
                            ? 'var(--color-primary-light)'
                            : 'var(--color-bg-tertiary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: intentSignals.engagementDepth?.enabled ? 1 : 0.4
                    }}
                >
                    <Eye size={14} style={{
                        color: intentSignals.engagementDepth?.enabled
                            ? 'var(--color-primary)'
                            : 'var(--color-text-tertiary)'
                    }} />
                </div>
                <div
                    title="Website Behavior"
                    style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: 'var(--radius-sm)',
                        background: intentSignals.websiteBehavior?.enabled
                            ? 'var(--color-info-light, var(--color-info))'
                            : 'var(--color-bg-tertiary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: intentSignals.websiteBehavior?.enabled ? 1 : 0.4
                    }}
                >
                    <Globe size={14} style={{
                        color: intentSignals.websiteBehavior?.enabled
                            ? 'var(--color-info)'
                            : 'var(--color-text-tertiary)'
                    }} />
                </div>
                <div
                    title="Recency & Frequency"
                    style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: 'var(--radius-sm)',
                        background: intentSignals.recencyFrequency?.enabled
                            ? 'var(--color-warning-light, var(--color-warning))'
                            : 'var(--color-bg-tertiary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: intentSignals.recencyFrequency?.enabled ? 1 : 0.4
                    }}
                >
                    <Clock size={14} style={{
                        color: intentSignals.recencyFrequency?.enabled
                            ? 'var(--color-warning)'
                            : 'var(--color-text-tertiary)'
                    }} />
                </div>
                <div
                    title="Stage Progression"
                    style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: 'var(--radius-sm)',
                        background: intentSignals.stageProgression?.enabled
                            ? 'var(--color-success-light, var(--color-success))'
                            : 'var(--color-bg-tertiary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: intentSignals.stageProgression?.enabled ? 1 : 0.4
                    }}
                >
                    <TrendingUp size={14} style={{
                        color: intentSignals.stageProgression?.enabled
                            ? 'var(--color-success)'
                            : 'var(--color-text-tertiary)'
                    }} />
                </div>
            </div>

            {/* Size and Source Info */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        Est. Size
                    </span>
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)'
                    }}>
                        {formatSize(estimatedSize)} users
                    </span>
                </div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <span style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        Sources
                    </span>
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-1)'
                    }}>
                        {sourceStages.map(stage => (
                            <span
                                key={stage}
                                style={{
                                    padding: '1px 6px',
                                    background: `${stageColors[stage]}20`,
                                    color: stageColors[stage],
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: '10px',
                                    fontWeight: 600
                                }}
                            >
                                {stage}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Platform Compatibility Indicators */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                marginTop: 'var(--space-2)',
                paddingTop: 'var(--space-2)',
                borderTop: '1px dashed var(--color-border)'
            }}>
                <span style={{
                    fontSize: '9px',
                    color: 'var(--color-text-tertiary)',
                    marginRight: 'var(--space-1)'
                }}>
                    Platforms:
                </span>
                {Object.entries(PLATFORM_COMPATIBILITY[segmentType] || {}).map(([platform, supported]) => (
                    <div
                        key={platform}
                        title={`${PLATFORM_LABELS[platform]}: ${supported ? 'Supported' : 'Not supported'}`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '20px',
                            height: '20px',
                            borderRadius: 'var(--radius-sm)',
                            background: supported ? `${PLATFORM_COLORS[platform]}15` : 'var(--color-bg-tertiary)',
                            border: `1px solid ${supported ? PLATFORM_COLORS[platform] : 'var(--color-border)'}`,
                            opacity: supported ? 1 : 0.4
                        }}
                    >
                        {supported ? (
                            <Check size={10} style={{ color: PLATFORM_COLORS[platform] }} />
                        ) : (
                            <X size={10} style={{ color: 'var(--color-text-tertiary)' }} />
                        )}
                    </div>
                ))}
            </div>

            {/* Source handle (bottom) - connects to ad sets/campaigns for targeting */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="target-out"
                style={{
                    background: intentColors[intentStrength],
                    width: '10px',
                    height: '10px'
                }}
            />
        </div>
    );
});

SegmentNode.displayName = 'SegmentNode';

export default SegmentNode;
